import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag, Eye, Check, X, Loader } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import './Reports.scss';

const KERALA_DISTRICTS = [
    'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 'Kollam',
    'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 'Pathanamthitta',
    'Thiruvananthapuram', 'Thrissur', 'Wayanad'
];

const CATEGORY_LABELS = {
    wrong_info: 'Wrong information',
    closed: 'Mosque closed',
    inappropriate_content: 'Inappropriate content',
    other: 'Other'
};

const Reports = () => {
    const { success, error } = useSnackbar();
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        mosqueID: '',
        status: '',
        district: '',
        fromDate: '',
        toDate: '',
        page: 1,
        limit: 20
    });
    const [pagination, setPagination] = useState({});
    const [updatingId, setUpdatingId] = useState(null);

    useEffect(() => {
        fetchReports();
    }, [filters]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await api.get(`/admin/reports?${params.toString()}`);
            if (response.data.success) {
                setReports(response.data.reports);
                setPagination(response.data.pagination || {});
            }
        } catch (err) {
            console.error('Error fetching reports:', err);
            error(err.response?.data?.message || 'Failed to fetch reports');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (reportId, newStatus) => {
        setUpdatingId(reportId);
        try {
            const response = await api.patch(`/admin/reports/${reportId}`, { status: newStatus });
            if (response.data.success) {
                setReports(prev => prev.map(r => r._id === reportId ? { ...r, status: newStatus } : r));
                success(`Report marked as ${newStatus}`);
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to update report');
        } finally {
            setUpdatingId(null);
        }
    };

    const goToMosque = (mosqueID) => {
        navigate(`/mosques/${mosqueID}`);
    };

    return (
        <div className="reports-page">
            <div className="page-header">
                <h2>
                    <Flag size={24} />
                    Mosque Reports
                </h2>
            </div>

            <div className="filters">
                <input
                    type="text"
                    placeholder="Mosque ID"
                    value={filters.mosqueID}
                    onChange={(e) => setFilters({ ...filters, mosqueID: e.target.value, page: 1 })}
                    className="filter-input"
                />
                <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                </select>
                <select
                    value={filters.district}
                    onChange={(e) => setFilters({ ...filters, district: e.target.value, page: 1 })}
                >
                    <option value="">All Districts</option>
                    {KERALA_DISTRICTS.map(d => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>
                <input
                    type="date"
                    placeholder="From date"
                    value={filters.fromDate}
                    onChange={(e) => setFilters({ ...filters, fromDate: e.target.value, page: 1 })}
                    className="filter-input"
                />
                <input
                    type="date"
                    placeholder="To date"
                    value={filters.toDate}
                    onChange={(e) => setFilters({ ...filters, toDate: e.target.value, page: 1 })}
                    className="filter-input"
                />
            </div>

            {loading ? (
                <div className="loading">
                    <Loader size={24} className="spin" />
                    Loading reports...
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="reports-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Mosque</th>
                                    <th>Category</th>
                                    <th>Description</th>
                                    <th>Reporter</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="empty-state">No reports found</td>
                                    </tr>
                                ) : (
                                    reports.map((report) => (
                                        <tr key={report._id}>
                                            <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="link-btn"
                                                    onClick={() => goToMosque(report.mosqueID)}
                                                >
                                                    {report.mosqueName || report.mosqueID} ({report.mosqueID})
                                                </button>
                                            </td>
                                            <td>{CATEGORY_LABELS[report.category] || report.category}</td>
                                            <td className="description-cell">{report.description}</td>
                                            <td>
                                                {report.reporter?.name}
                                                {report.reporter?.email && (
                                                    <span className="reporter-email"> ({report.reporter.email})</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`status-badge status-${report.status}`}>
                                                    {report.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    {report.status !== 'resolved' && (
                                                        <button
                                                            type="button"
                                                            className="action-btn resolve"
                                                            onClick={() => handleStatusUpdate(report._id, 'resolved')}
                                                            disabled={updatingId === report._id}
                                                            title="Mark resolved"
                                                        >
                                                            {updatingId === report._id ? <Loader size={14} className="spin" /> : <Check size={14} />}
                                                            Resolve
                                                        </button>
                                                    )}
                                                    {report.status !== 'dismissed' && (
                                                        <button
                                                            type="button"
                                                            className="action-btn dismiss"
                                                            onClick={() => handleStatusUpdate(report._id, 'dismissed')}
                                                            disabled={updatingId === report._id}
                                                            title="Dismiss"
                                                        >
                                                            <X size={14} />
                                                            Dismiss
                                                        </button>
                                                    )}
                                                    {report.status !== 'pending' && (
                                                        <button
                                                            type="button"
                                                            className="action-btn pending"
                                                            onClick={() => handleStatusUpdate(report._id, 'pending')}
                                                            disabled={updatingId === report._id}
                                                            title="Reopen"
                                                        >
                                                            <Eye size={14} />
                                                            Reopen
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                disabled={filters.page <= 1}
                                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                            >
                                Previous
                            </button>
                            <span>Page {filters.page} of {pagination.pages}</span>
                            <button
                                disabled={filters.page >= pagination.pages}
                                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Reports;
